import React, { useEffect, useMemo, useState } from 'react';

import { fetchMeasurementTraces } from '../../services/batchService';
import {
  cloneJsonValue,
  DERIVED_PREVIEW_STATUS,
  getDerivedSeedEffectiveTraces,
  getDerivedSeedExecutionSetupDetail,
  getDerivedSeedParams,
  getDerivedSeedRequestedTraces,
  getDerivedSeedSampleLabel,
  getDerivedSeedStartedAt,
  getPreviewExecutionSetupDetail,
  hasDerivedDraftChanges,
  hasFreshDerivedPreview,
  hasRequestedTraceDraftChangesForSeed,
} from '../../utils/derivedRunDraft';
import { extractApiError } from '../../utils/experimentalSetups';
import { formatRunTimestamp } from '../../utils/runFormatting';
import { EncapsulationRunView } from './EncapsulationRunView';
import { SizeRunView } from './SizeRunView';
import SizePlot from '../SizePlot';
import { DerivedSizeTiming, expectedT0Line, getExpectedT0 } from './DerivedSizeTiming';
import { DerivedSizeParamsEditor, getSizeDraftError } from './DerivedSizeParamsEditor';
import { StatusBadge } from './components/StatusBadge';

const RERUN_REASON_OPTIONS = [
  { value: 'PARAM_CHANGE', label: 'Parameter change' },
  { value: 'TRACE_CHANGE', label: 'Trace selection change' },
  { value: 'CONFIRMATION', label: 'Confirmation' },
  { value: 'INVESTIGATION', label: 'Investigation' },
  { value: 'METHOD_UPDATE', label: 'Method update' },
  { value: 'OTHER', label: 'Other' },
];

const REASONS_REQUIRING_TEXT = new Set(['INVESTIGATION', 'METHOD_UPDATE', 'OTHER']);
const ENCAPSULATION_PARAM_TYPES = Object.freeze({
  prominence: 'number',
  width_factor: 'number',
});
const ACTIVE_EXECUTION_STATUSES = new Set([
  'pending',
  'planned',
  'processing',
  'running',
]);

function getDerivedExecutionPresentation(executionRequest) {
  const planItem = Array.isArray(executionRequest?.plan_items)
    ? executionRequest.plan_items[0] || null
    : null;
  const rawStatus = String(
    planItem?.status ||
      planItem?.latest_attempt?.status ||
      executionRequest?.status ||
      ''
  ).toLowerCase();
  const status = ['planned', 'processing'].includes(rawStatus)
    ? 'pending'
    : rawStatus;
  const failure =
    planItem?.last_error ||
    planItem?.latest_attempt?.failure_summary ||
    executionRequest?.failure_summary ||
    null;
  const failureMessage =
    typeof failure === 'string'
      ? failure
      : failure?.message || failure?.detail || failure?.error || null;

  return {
    failureMessage,
    isActive: ACTIVE_EXECUTION_STATUSES.has(rawStatus),
    status,
  };
}

function JsonCard({ title, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      <pre className="mt-3 overflow-x-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function DiffListCard({ title, items, renderItem }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      {!items.length ? (
        <p className="mt-3 text-sm text-slate-500">No changes.</p>
      ) : (
        <ul className="mt-3 space-y-3 text-sm text-slate-700">
          {items.map((item, idx) => (
            <li key={`${title}-${idx}`} className="rounded border border-slate-200 p-3">
              {renderItem(item)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TraceSummaryTable({ title, traces }) {
  const rows = Array.isArray(traces) ? traces : [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      {!rows.length ? (
        <p className="mt-3 text-sm text-slate-500">No trace data recorded.</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Input</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Trace</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Wavelength</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Signal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {rows.map((trace) => (
                <tr key={`${title}-${trace.import_id}`}>
                  <td className="px-3 py-2 text-slate-700">
                    <div className="font-medium">Import {trace.import_id}</div>
                    <div className="text-xs text-slate-500">
                      {trace.filename || trace.file_role || 'Frozen provenance input'}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{trace.trace_id || '—'}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {trace.wavelength_nm != null ? `${trace.wavelength_nm} nm` : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {trace.signal_unit_canonical || trace.signal_unit_raw || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ArtifactListCard({ artifacts }) {
  const items = Array.isArray(artifacts) ? artifacts : [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-900">Artifacts</h4>
      {!items.length ? (
        <p className="mt-3 text-sm text-slate-500">No artifacts returned.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {items.map((artifact, idx) => (
            <details key={`${artifact.artifact_type}-${idx}`} className="rounded border border-slate-200 p-3">
              <summary className="cursor-pointer text-sm font-medium text-slate-800">
                {artifact.artifact_type} • {artifact.artifact_category}
              </summary>
              <pre className="mt-3 overflow-x-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
                {JSON.stringify(artifact.payload, null, 2)}
              </pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function formatTraceOptionLabel(trace) {
  const parts = [];
  if (trace.trace_id) parts.push(trace.trace_id);
  if (trace.wavelength_nm != null) parts.push(`${trace.wavelength_nm} nm`);
  if (trace.signal_unit_raw) parts.push(trace.signal_unit_raw);
  if (trace.is_analyzable === true) {
    parts.push('Analyzable');
  } else if (trace.is_analyzable === false) {
    parts.push('Instrument channel');
  }
  return parts.join(' • ') || 'Trace';
}

function encodeTraceOption(trace) {
  return JSON.stringify({
    trace_id: trace.trace_id ?? null,
    wavelength_nm: trace.wavelength_nm ?? null,
  });
}

function traceMatches(a, b) {
  return (
    String(a?.trace_id || '') === String(b?.trace_id || '') &&
    String(a?.wavelength_nm ?? '') === String(b?.wavelength_nm ?? '')
  );
}

function coerceDraftValue(rawValue, intendedType) {
  if (intendedType === 'boolean') {
    return Boolean(rawValue);
  }
  if (intendedType === 'number') {
    if (typeof rawValue === 'string' && rawValue.trim() === '') {
      return rawValue;
    }
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : rawValue;
  }
  return rawValue;
}

function EncapsulationParamsEditor({ value, onChange, disabled }) {
  const updateField = (fieldKey, rawValue) => {
    const nextDraft = cloneJsonValue(value);
    nextDraft[fieldKey] = coerceDraftValue(
      rawValue,
      ENCAPSULATION_PARAM_TYPES[fieldKey]
    );
    onChange(nextDraft);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-900">Encapsulation params</h4>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Prominence</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={value?.prominence ?? ''}
            onChange={(event) => updateField('prominence', event.target.value)}
            disabled={disabled}
            className="w-full rounded border border-slate-300 p-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Width factor</span>
          <input
            type="number"
            step="0.1"
            min="0"
            value={value?.width_factor ?? ''}
            onChange={(event) => updateField('width_factor', event.target.value)}
            disabled={disabled}
            className="w-full rounded border border-slate-300 p-2 text-sm"
          />
        </label>
      </div>
    </div>
  );
}

function PreviewPanel({ sourceRunSeed, preview }) {
  const sharedProps = {
    sampleName: getDerivedSeedSampleLabel(sourceRunSeed),
    status: 'preview',
    params: preview.effective_params,
    footerNote:
      'Exploratory preview is transient. It is not a saved run and it is never inserted into the run list.',
  };

  if (preview.pipeline === 'encapsulation') {
    return (
      <EncapsulationRunView
        {...sharedProps}
        executionSetup={
          getPreviewExecutionSetupDetail(preview) ||
          getDerivedSeedExecutionSetupDetail(sourceRunSeed)
        }
        encapsulationEfficiency={preview.result?.encapsulation_efficiency ?? null}
        totalRegion={preview.result?.region_total ?? null}
        totalFit={preview.result?.fitted_total ?? null}
        freeRegion={preview.result?.region_free ?? null}
        freeFit={preview.result?.fitted_free ?? null}
        failureMessage={preview.result?.error || null}
      />
    );
  }

  if (preview.pipeline === 'size') {
    const components = preview.result?.components || [];
    const expected = getExpectedT0(components[0], sourceRunSeed?.source_expected_t0);
    const fitted = [...new Set(components.map((component) => component.t0).filter((value) => typeof value === 'number' && Number.isFinite(value)))];
    return (
      <>
      <DerivedSizeTiming components={components} expectedT0={expected} />
      <SizeRunView
        {...sharedProps}
        taylorgramReferenceLines={[...expectedT0Line(expected), ...fitted.map((value, i) => ({
          key: `fitted-${i}`, x: value, stroke: '#2563eb',
          label: { value: `Fitted t0 ${value.toFixed(2)} s`, position: 'insideTopRight', fontSize: 11 },
        }))]}
        compactResults
        showDetectionControls={false}
        showSampleSummary={false}
        components={preview.result?.components || []}
        messages={preview.result?.messages || []}
        plots={preview.result?.plots || null}
        failureMessage={preview.result?.error || null}
      />
      </>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-900">Preview result</h4>
      <pre className="mt-3 overflow-x-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
        {JSON.stringify(preview.result, null, 2)}
      </pre>
    </div>
  );
}

export function DerivedRunExplorer({
  batchId,
  draft,
  onChangeParamDraft,
  onChangeRequestedTraceDraft,
  onChangeRerunReasonCode,
  onChangeRerunReasonText,
  onPreview,
  onSave,
  onCancel,
  executionRequest = null,
  publicationFailure = null,
  pollingError = null,
}) {
  const [traceOptionsByImportId, setTraceOptionsByImportId] = useState({});
  const [traceOptionsLoading, setTraceOptionsLoading] = useState(false);
  const [traceOptionsError, setTraceOptionsError] = useState(null);

  const sourceRequestedTraces = useMemo(
    () => getDerivedSeedRequestedTraces(draft.sourceRunSeed),
    [draft.sourceRunSeed]
  );
  const sourceEffectiveTraces = useMemo(
    () => getDerivedSeedEffectiveTraces(draft.sourceRunSeed),
    [draft.sourceRunSeed]
  );
  const traceChanged = hasRequestedTraceDraftChangesForSeed(
    draft.sourceRunSeed,
    draft.requestedTraceDraft
  );
  const hasChanges = hasDerivedDraftChanges(draft);
  const previewStatus = draft.previewStatus || DERIVED_PREVIEW_STATUS.IDLE;
  const hasFreshPreview = hasFreshDerivedPreview(draft);
  const rerunTextRequired = REASONS_REQUIRING_TEXT.has(draft.rerunReasonCode);
  const executionPresentation = getDerivedExecutionPresentation(executionRequest);
  const editorDisabled = draft.saveLoading || executionPresentation.isActive;
  const paramError = draft.pipeline === 'size' ? getSizeDraftError(draft.paramDraft) : null;
  const sourceTaylorgram = draft.sourceRunSeed?.source_taylorgram;
  const sourcePlotData = useMemo(() => Array.isArray(sourceTaylorgram?.time)
    ? sourceTaylorgram.time.map((time, i) => ({ time, signal: sourceTaylorgram.signal?.[i] ?? null })) : [], [sourceTaylorgram]);
  const manualWindow = draft.paramDraft?.region?.strategy === 'declared_window';
  const selectionAllowed = !traceChanged && !editorDisabled;
  const selectedWindow = manualWindow && !traceChanged ? {
    start: draft.paramDraft.region.window_start_s, end: draft.paramDraft.region.window_end_s,
  } : null;
  const canPreview =
    !paramError &&
    hasChanges &&
    !editorDisabled &&
    previewStatus !== DERIVED_PREVIEW_STATUS.LOADING &&
    !hasFreshPreview;
  const canSave =
    !paramError &&
    hasFreshPreview &&
    !editorDisabled &&
    (!rerunTextRequired || Boolean(draft.rerunReasonText.trim()));

  useEffect(() => {
    console.log('[DerivedRunExplorer lifecycle] mount', {
      batchId,
      sourceRunId: draft?.sourceRunId ?? null,
    });
    return () => {
      console.log('[DerivedRunExplorer lifecycle] unmount', {
        batchId,
        sourceRunId: draft?.sourceRunId ?? null,
      });
    };
  }, []);

  useEffect(() => {
    let active = true;
    const importIds = draft.requestedTraceDraft.map((trace) => trace.import_id);

    setTraceOptionsLoading(true);
    setTraceOptionsError(null);
    setTraceOptionsByImportId({});

    Promise.allSettled(
      importIds.map((importId) => fetchMeasurementTraces(batchId, importId))
    )
      .then((results) => {
        if (!active) return;
        const nextOptions = {};
        const errors = [];

        results.forEach((result, index) => {
          const importId = importIds[index];
          if (result.status === 'fulfilled') {
            nextOptions[importId] = result.value;
          } else {
            errors.push(`Import ${importId}: ${extractApiError(result.reason, 'Failed to load traces.')}`);
          }
        });

        setTraceOptionsByImportId(nextOptions);
        if (errors.length) {
          setTraceOptionsError(errors.join(' '));
        }
      })
      .finally(() => {
        if (active) {
          setTraceOptionsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [batchId, draft.requestedTraceDraft]);

  const handleParamDraftChange = (nextParamDraft) => {
    if (editorDisabled) return;
    onChangeParamDraft(nextParamDraft);
  };

  const handleTraceDraftChange = (importId, nextTrace) => {
    if (editorDisabled) return;
    const nextDraft = draft.requestedTraceDraft.map((trace) =>
      Number(trace.import_id) === Number(importId)
        ? {
            import_id: trace.import_id,
            trace_id: nextTrace.trace_id,
            wavelength_nm: nextTrace.wavelength_nm,
          }
        : trace
    );
    onChangeRequestedTraceDraft(nextDraft);
  };

  const handleReasonCodeChange = (nextCode) => {
    if (editorDisabled) return;
    onChangeRerunReasonCode(nextCode);
    if (!REASONS_REQUIRING_TEXT.has(nextCode)) {
      onChangeRerunReasonText('');
    }
  };

  const renderTraceEditors = () => (
    <div>
      <h4 className="text-xs font-medium text-slate-700">Trace for derived run</h4>
      {traceOptionsError && (
        <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {traceOptionsError}
        </p>
      )}
      <div className="mt-1 space-y-2">
        {draft.requestedTraceDraft.map((trace) => {
          const traceResponse = traceOptionsByImportId[trace.import_id];
          const options = Array.isArray(traceResponse?.traces) ? traceResponse.traces : [];
          const matchedOption = options.find((option) => traceMatches(option, trace));
          const selectedValue = matchedOption
            ? encodeTraceOption(matchedOption)
            : encodeTraceOption(trace);

          return (
            <div
              key={`trace-draft-${trace.import_id}`}
              className="min-w-0"
            >
              <div className="mb-2">
                <div className="text-sm font-medium text-slate-900">
                  Import {trace.import_id}
                </div>
                <div className="text-xs text-slate-500">
                  {
                    sourceRequestedTraces.find(
                      (item) => Number(item.import_id) === Number(trace.import_id)
                    )?.filename || 'Frozen provenance input'
                  }
                </div>
              </div>
              <select
                aria-label={`Trace for import ${trace.import_id}`}
                value={selectedValue}
                onChange={(event) => {
                  const nextTrace = JSON.parse(event.target.value);
                  handleTraceDraftChange(trace.import_id, nextTrace);
                }}
                disabled={editorDisabled || traceOptionsLoading || !options.length}
                className="w-full rounded border border-slate-300 p-2 text-sm"
              >
                {!matchedOption && (
                  <option value={selectedValue}>
                    Current draft • {trace.trace_id || '—'} •{' '}
                    {trace.wavelength_nm != null ? `${trace.wavelength_nm} nm` : '—'}
                  </option>
                )}
                {options.map((option) => (
                  <option
                    key={`${trace.import_id}-${option.trace_id || option.wavelength_nm}`}
                    value={encodeTraceOption(option)}
                  >
                    {formatTraceOptionLabel(option)}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderPreviewState = () => {
    if (previewStatus === DERIVED_PREVIEW_STATUS.LOADING && !draft.previewResponse) {
      return (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
          Running exploratory preview…
        </div>
      );
    }

    if (previewStatus === DERIVED_PREVIEW_STATUS.ERROR && !draft.previewResponse) {
      return (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow-sm">
          {draft.previewError}
        </div>
      );
    }

    if (!draft.previewResponse) {
      return (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
          Change parameters or trace, then click Run preview. Preview results are not saved automatically.
        </div>
      );
    }

    const preview = draft.previewResponse;
    let previewBanner = null;

    if (previewStatus === DERIVED_PREVIEW_STATUS.LOADING) {
      previewBanner = (
        <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-700 shadow-sm">
          Refreshing preview for the current draft. Save stays disabled until the latest response arrives.
        </div>
      );
    } else if (previewStatus === DERIVED_PREVIEW_STATUS.STALE) {
      previewBanner = (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700 shadow-sm">
          Outdated preview — the preview below is stale for the current draft. Edit settings and click Run preview again before saving.
        </div>
      );
    } else if (previewStatus === DERIVED_PREVIEW_STATUS.ERROR) {
      previewBanner = (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 shadow-sm">
          {draft.previewError} The last successful preview is shown below for reference only and cannot be saved.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {previewBanner}
        <div className="flex items-center justify-between gap-2 border-b pb-2">
          <h4 className="text-sm font-semibold">Unsaved preview results</h4>
          <span className="text-xs text-slate-500">{hasFreshPreview ? 'Current draft' : 'Previous draft'}</span>
        </div>
        <PreviewPanel sourceRunSeed={draft.sourceRunSeed} preview={preview} />
        <details className="rounded-lg border p-4"><summary className="cursor-pointer font-medium">Preview technical details</summary>
        <div className="grid gap-4 xl:grid-cols-3">
          <JsonCard title="Effective params" value={preview.effective_params} />
          <JsonCard title="Detailed result" value={preview.result} />
          <TraceSummaryTable title="Requested traces" traces={preview.requested_traces} />
          <TraceSummaryTable title="Effective traces" traces={preview.effective_traces} />
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <DiffListCard
            title="Param diff"
            items={preview.param_diff || []}
            renderItem={(item) => (
              <div className="space-y-1">
                <div className="font-medium text-slate-900">{item.path}</div>
                <div className="text-xs text-slate-500">Before: {JSON.stringify(item.before)}</div>
                <div className="text-xs text-slate-500">After: {JSON.stringify(item.after)}</div>
              </div>
            )}
          />
          <DiffListCard
            title="Trace diff"
            items={preview.trace_diff || []}
            renderItem={(item) => (
              <div className="space-y-1">
                <div className="font-medium text-slate-900">Import {item.import_id}</div>
                <div className="text-xs text-slate-500">
                  Before: {JSON.stringify(item.before)}
                </div>
                <div className="text-xs text-slate-500">
                  After: {JSON.stringify(item.after)}
                </div>
              </div>
            )}
          />
          <ArtifactListCard artifacts={preview.artifacts} />
        </div>

        </details>

      </div>
    );
  };

  return (
    <div className="space-y-3">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900">{getDerivedSeedSampleLabel(draft.sourceRunSeed)} • Derived analysis</h3>
        <p className="text-xs text-slate-500">Source run {draft.sourceRunId} • {formatRunTimestamp(getDerivedSeedStartedAt(draft.sourceRunSeed))}</p>
      </header>
      <div data-testid="derived-workspace" className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.85fr)_minmax(300px,1fr)]">
        <main aria-label="Analysis workspace" className="min-w-0 space-y-3">
      {draft.pipeline === 'size' && <section aria-label="Source evidence" className="rounded-lg border bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3"><h4 className="text-sm font-semibold">Source trace and original Taylorgram</h4><div className="min-w-0 flex-1 max-w-sm">{renderTraceEditors()}</div></div>
        <p className="text-sm text-slate-600">Saved source evidence — unchanged by draft edits or trace selection.</p>
        {sourceEffectiveTraces.map((trace) => <p key={trace.import_id} className="text-sm">
          {trace.filename || `Import ${trace.import_id}`} • {trace.trace_id || 'Unknown trace'} • {trace.wavelength_nm != null ? `${trace.wavelength_nm} nm` : 'No wavelength'} • {trace.signal_unit_canonical || trace.signal_unit_raw || 'Signal units not recorded'}
        </p>)}
        {sourcePlotData.length ? <SizePlot title="Original saved Taylorgram" data={sourcePlotData}
          selectionEnabled={selectionAllowed}
          selectionKey={JSON.stringify([draft.sourceRunId, draft.requestedTraceDraft])}
          selectedWindow={selectedWindow}
          onSelectWindow={({ start, end }) => {
            if (!selectionAllowed) return;
            const next = cloneJsonValue(draft.paramDraft);
            next.region = { ...next.region, strategy: 'declared_window', window_start_s: start, window_end_s: end };
            handleParamDraftChange(next);
          }}
          referenceLines={expectedT0Line(draft.sourceRunSeed?.source_expected_t0)}
          lines={[{ dataKey: 'signal', name: 'Saved source signal', color: '#64748b' }]} />
          : <p className="text-sm text-slate-500">No Taylorgram was stored with this source run.</p>}
        {selectionAllowed && <p className="text-sm text-slate-600">Drag across the saved trace to set the fitting window. Boundaries snap to recorded plot timestamps on release; Start/End remain editable.</p>}
        {traceChanged && manualWindow && <p className="text-sm text-amber-700">Selection on source evidence is disabled for a different trace. Enter numeric Start/End and Run preview to validate the window against the requested trace.{hasFreshPreview ? ' The current preview has validated this window.' : ''}</p>}
        {traceChanged && <p className="text-sm text-amber-700">The draft requests a different trace. This graph still shows the saved source; Run preview to see the requested trace.</p>}
      </section>}

          {draft.pipeline !== 'size' && renderTraceEditors()}
          <section aria-label="Preview results" className="min-w-0 space-y-3">
            {renderPreviewState()}
          </section>
          <details className="rounded-lg border p-4"><summary className="cursor-pointer font-medium">Source technical details</summary>
          <div className="grid gap-4">
            <JsonCard
              title="Source params"
              value={getDerivedSeedParams(draft.sourceRunSeed) || {}}
            />
            <TraceSummaryTable title="Source requested traces" traces={sourceRequestedTraces} />
            <TraceSummaryTable title="Source effective traces" traces={sourceEffectiveTraces} />
          </div></details>
        </main>
        <aside aria-label="Fitting settings" className="min-w-0 rounded-lg border border-slate-200 bg-white p-3 space-y-3">
          <h4 className="text-sm font-semibold">Fitting settings</h4>
          {draft.pipeline === 'encapsulation' ? (
            <EncapsulationParamsEditor value={draft.paramDraft} onChange={handleParamDraftChange} disabled={editorDisabled} />
          ) : (
            <DerivedSizeParamsEditor value={draft.paramDraft} sourceParams={getDerivedSeedParams(draft.sourceRunSeed)}
              onChange={handleParamDraftChange} disabled={editorDisabled} />
          )}
          <div className="sticky bottom-0 z-10 border-t bg-white pt-3 space-y-2">
            {paramError && <p role="alert" className="text-xs text-rose-700">{paramError}</p>}
            <p role="status" className="text-xs text-slate-600">
              {previewStatus === DERIVED_PREVIEW_STATUS.LOADING ? 'Preview running. You can continue editing.'
                : hasFreshPreview ? 'Preview is current. Edit any setting to run another preview.'
                : draft.previewResponse ? 'Outdated preview. Run preview to evaluate your changes.'
                : 'Edit settings or select a region, then run preview.'}
            </p>
            <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onPreview}
              disabled={!canPreview}
              className="rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {previewStatus === DERIVED_PREVIEW_STATUS.LOADING
                ? 'Previewing…'
                : 'Run preview'}
            </button>            <button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {executionPresentation.status === 'running'
                ? 'Running…'
                : executionPresentation.status === 'pending'
                  ? 'Queued…'
                  : draft.saveLoading
                    ? 'Saving…'
                    : 'Save as derived run'}
            </button>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-3">
            <h4 className="text-sm font-semibold text-slate-900">Save reason</h4>
            <div className="mt-2 space-y-2">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  Rerun reason code
                </span>
                <select
                  value={draft.rerunReasonCode}
                  onChange={(event) => handleReasonCodeChange(event.target.value)}
                  disabled={editorDisabled}
                  className="w-full rounded border border-slate-300 p-2 text-sm"
                >
                  {RERUN_REASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">
                  Rerun reason text
                </span>
                <textarea
                  value={draft.rerunReasonText}
                  onChange={(event) => {
                    if (!editorDisabled) {
                      onChangeRerunReasonText(event.target.value);
                    }
                  }}
                  disabled={editorDisabled}
                  rows={2}
                  className="w-full rounded border border-slate-300 p-2 text-sm"
                  placeholder={
                    rerunTextRequired
                      ? 'Required for this reason code.'
                      : 'Optional explanation.'
                  }
                />
              </label>
              {rerunTextRequired && !draft.rerunReasonText.trim() && (
                <p className="text-sm text-amber-700">
                  This reason code requires rerun reason text before saving.
                </p>
              )}
            </div>
          </div>


          {draft.saveError && <p role="alert" className="text-sm text-rose-700">{draft.saveError}</p>}
            {publicationFailure && (
              <div
                role="alert"
                className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
              >
                The derived execution request was saved, but Celery publication{' '}
                {publicationFailure.status === 'partial_failure'
                  ? 'failed for some analyses.'
                  : 'failed.'}{' '}
                The affected analysis remains pending and can be safely recovered.
              </div>
            )}
            {executionRequest?.execution_request_id && (
              <div className="mt-3 rounded border border-sky-200 bg-sky-50 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-slate-900">
                    Derived execution
                  </span>
                  <StatusBadge status={executionPresentation.status || 'pending'} />
                </div>
                {executionPresentation.status === 'pending' && (
                  <p className="mt-1 text-xs text-slate-600">
                    Queued and waiting for a worker.
                  </p>
                )}
                {executionPresentation.status === 'running' && (
                  <p className="mt-1 text-xs text-slate-600">
                    The derived analysis is running.
                  </p>
                )}
                {executionPresentation.status === 'failed' && (
                  <p className="mt-1 text-xs text-rose-700">
                    {executionPresentation.failureMessage ||
                      'The derived analysis failed.'}
                  </p>
                )}
              </div>
            )}
            {pollingError && (
              <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Live execution status is temporarily unavailable:{' '}
                {extractApiError(pollingError, 'Unable to refresh status.')}
              </p>
            )}

          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <button type="button" disabled={editorDisabled} className="rounded border px-3 py-2 text-sm disabled:opacity-50"
              onClick={() => {
                handleParamDraftChange(cloneJsonValue(getDerivedSeedParams(draft.sourceRunSeed)));
                onChangeRequestedTraceDraft(sourceRequestedTraces.map(({ import_id, trace_id, wavelength_nm }) => ({ import_id, trace_id, wavelength_nm })));
              }}>Reset to original</button>
            <button
              type="button"
              onClick={onCancel}
              disabled={executionPresentation.isActive}
              className="rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel exploration
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
